/**
 * hs-core.js — HighSpark first-party storefront tracker
 * (renamed from hs-tracking.js: "tracking" in filenames/paths is one character
 * away from EasyPrivacy filter patterns; neutral naming keeps the pixel out of
 * adblock lists' generic rules. Endpoint moved to /api/hs/e for the same reason.)
 *
 * Captures UTMs + paid-click IDs (gclid/gbraid/wbraid/fbclid) at landing and
 * gets them onto the Shopify order two ways:
 *   1. DETERMINISTIC (primary): writes them into the cart as cart attributes
 *      via POST /cart/update.js, so they land directly on the order's
 *      note_attributes — the Elevar/Analyzify pattern. Survives GoKwik's
 *      checkout redirect and Safari's 7-day storage purge (written at ATC time,
 *      not read from 90-day storage at conversion).
 *   2. FALLBACK: a first-party event stream to the ERP, joined to the order
 *      server-side by the Shopify `cart` token.
 * Also mirrors GoKwik's own dataLayer events (checkout-initiated / order-placed
 * / gk_purchase) so checkout + purchase signals don't depend on brittle DOM
 * guessing or a Shopify thank-you page that never renders under GoKwik.
 *
 * MASTER SWITCH — DORMANT BY DEFAULT. This script does NOTHING unless
 *   window.HS_TRACKING_ENABLED === true
 * is set before it loads. It can therefore ship to the live theme staged but
 * inert; activate by setting that flag (a one-line theme change) when ready.
 *
 * PURCHASE SELF-FIRE (optional, config-gated): set in theme.liquid
 *   window.HS_GA4_ID      = 'G-XXXXXXXXXX';           // GA4 purchase w/ value
 *   window.HS_ADS_SEND_TO = 'AW-XXXXXXXX/AbCdEfGh';   // Ads valued conversion
 * On GoKwik order success (announced to THIS page before the mobile redirect to
 * the untaggable accounts subdomain) the pixel fires GA4 purchase + Ads
 * conversion with real value + transaction_id itself. Both platforms dedup on
 * transaction_id, so this is safe alongside the desktop channel-pixel firing.
 *
 * Install: add to theme.liquid before </body>
 *   <script>window.HS_TRACKING_ENABLED = true;</script>   // when going live
 *   <script src="{{ 'hs-core.js' | asset_url }}" defer></script>
 *
 * Env: set window.HS_TRACKING_URL to override the ERP endpoint.
 */

(function () {
  "use strict";

  // ── Master switch — inert unless explicitly enabled ─────────────────────────
  if (window.HS_TRACKING_ENABLED !== true) return;

  var ENDPOINT =
    (typeof window.HS_TRACKING_URL === "string" && window.HS_TRACKING_URL) ||
    "https://api.highspark.in/api/hs/e";

  // Capture the pristine fetch up front — used for our own /cart/update.js call
  // so it never re-enters our observer wrapper below.
  var _origFetch = window.fetch;

  var SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity → new session
  var CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000; // Chrome-effective; Safari caps ~7d (mitigated by cart attributes)
  var ANON_KEY = "_hs_anon";
  var SESS_KEY = "_hs_sess";
  var CLICK_KEY = "_hs_click";

  // ── Identity ────────────────────────────────────────────────────────────────

  function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function getAnonId() {
    var id = null;
    try { id = localStorage.getItem(ANON_KEY); } catch (_) {}
    if (!id) {
      id = uuid();
      try { localStorage.setItem(ANON_KEY, id); } catch (_) {}
    }
    return id;
  }

  function getSession(utms) {
    var now = Date.now();
    try {
      var raw = sessionStorage.getItem(SESS_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (now - s.ts < SESSION_TTL_MS) {
          s.ts = now;
          sessionStorage.setItem(SESS_KEY, JSON.stringify(s));
          return s;
        }
      }
    } catch (_) {}
    var sess = {
      id: uuid(),
      ts: now,
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      utm_content: utms.utm_content || null,
      utm_term: utms.utm_term || null,
      landing_page: location.href,
      referrer: document.referrer || null,
    };
    try { sessionStorage.setItem(SESS_KEY, JSON.stringify(sess)); } catch (_) {}
    return sess;
  }

  // ── UTM + click-ID capture ──────────────────────────────────────────────────

  function parseUtms() {
    var params = new URLSearchParams(location.search);
    var keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    var out = {};
    keys.forEach(function (k) {
      var v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"));
    if (!m) return null;
    // decodeURIComponent throws URIError on a malformed %-sequence; a single junk
    // cookie must not kill the whole tracker — fall back to the raw value.
    try { return decodeURIComponent(m[1]); } catch (_) { return m[1]; }
  }

  // Google's Conversion Linker — built into gtag.js, live on this store as
  // AW-10862632638 — persists the last ad click in the first-party _gcl_aw
  // cookie for 90 days, formatted `GCL.<timestampSeconds>.<gclid>`.
  // We previously read ONLY location.search, so a visitor who clicked an ad on
  // Monday and returned on Thursday with a clean URL produced no click id at
  // all, even though Google still knew the click. That is the single largest
  // source of missing attribution (no-click orders average 187 min from session
  // to purchase vs 12 min for click-id orders — i.e. returning buyers).
  function gclidFromCookie() {
    var raw = getCookie("_gcl_aw");
    if (!raw) return null;
    var parts = raw.split(".");
    // GCL . <ts> . <gclid>; rejoin the tail in case a gclid ever contains a dot.
    if (parts.length < 3) return null;
    var id = parts.slice(2).join(".");
    return id || null;
  }

  function captureClick() {
    var params = new URLSearchParams(location.search);
    var gclid = params.get("gclid");
    var gbraid = params.get("gbraid");
    var wbraid = params.get("wbraid");
    var fbclid = params.get("fbclid");
    // srsltid = Merchant Center auto-tagging on FREE listing clicks — the
    // paid-vs-free discriminator (paid Shopping carries gclid instead).
    var srsltid = params.get("srsltid");
    var stored = storedClick();
    if (gclid || gbraid || wbraid || fbclid) {
      // Paid click → full overwrite (last-click-wins).
      stored = {
        gclid: gclid || null,
        gbraid: gbraid || null,
        wbraid: wbraid || null,
        fbclid: fbclid || null,
        srsltid: srsltid || null,
        captured_at: Date.now(),
      };
      try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
    } else if (srsltid && !(stored && (stored.gclid || stored.gbraid || stored.wbraid || stored.fbclid))) {
      // Free-listing click must never clobber a stored PAID click.
      stored = { gclid: null, gbraid: null, wbraid: null, fbclid: null, srsltid: srsltid, captured_at: Date.now() };
      try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
    }
    // No paid click id in THIS url — fall back to Google's own cookie. Strictly
    // additive: a click id we already hold (url or previously stored) always
    // wins, so this can only turn a "no click" into a "click", never rewrite one.
    if (!gclid && !gbraid && !wbraid && !fbclid &&
        !(stored && (stored.gclid || stored.gbraid || stored.wbraid || stored.fbclid))) {
      var cookieGclid = gclidFromCookie();
      if (cookieGclid) {
        stored = {
          gclid: cookieGclid,
          gbraid: null,
          wbraid: null,
          fbclid: null,
          srsltid: (stored && stored.srsltid) || srsltid || null,
          captured_at: Date.now(),
        };
        try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
      }
    }
    return stored;
  }

  function storedClick() {
    try {
      var raw = localStorage.getItem(CLICK_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (Date.now() - s.captured_at < CLICK_TTL_MS) return s;
      }
    } catch (_) {}
    return null;
  }

  // Meta fbc: prefer the _fbc cookie; else reconstruct fb.1.{captureMs}.{fbclid}
  // per Meta's spec (timestamp = when we first observed the fbclid). fbp cookie.
  function resolveMeta(click) {
    var fbc = getCookie("_fbc");
    if (!fbc && click && click.fbclid) {
      fbc = "fb.1." + (click.captured_at || Date.now()) + "." + click.fbclid;
    }
    return { fbc: fbc || null, fbp: getCookie("_fbp") };
  }

  // The Shopify `cart` cookie value is the cart token; since 2024/25 it carries a
  // `?key=…` suffix. Normalise to the base token (strip at `?`) so the server-side
  // join compares like-for-like against the order's note_attributes cart_token —
  // if either side ever keeps/drops the suffix differently, an exact-match join
  // would silently miss.
  function normalizeCartToken(t) {
    if (!t) return null;
    var s = String(t).trim();
    var q = s.indexOf("?");
    return q >= 0 ? s.slice(0, q) : s;
  }
  function getCartToken() {
    return normalizeCartToken(getCookie("cart"));
  }

  // ── Deterministic identity injection (the primary path) ─────────────────────
  // Write our session + click IDs into the cart as attributes. Shopify carries
  // cart attributes onto the order as note_attributes, and GoKwik preserves them
  // (that is where the order's own cart_token note comes from). The ERP join then
  // reads them DIRECTLY off the order — no probabilistic token lookup, immune to
  // storage purge. hs_ prefix namespaces them.
  var cartAttrsWritten = false;
  function setCartAttributes(force) {
    if (cartAttrsWritten && !force) return;
    var click = storedClick();
    var meta = resolveMeta(click);
    var attrs = { hs_session_id: sess.id, hs_anon_id: anonId };
    if (click) {
      if (click.gclid) attrs.hs_gclid = click.gclid;
      if (click.gbraid) attrs.hs_gbraid = click.gbraid;
      if (click.wbraid) attrs.hs_wbraid = click.wbraid;
      if (click.fbclid) attrs.hs_fbclid = click.fbclid;
      if (click.srsltid) attrs.hs_srsltid = click.srsltid;
      if (click.captured_at) attrs.hs_click_at = String(click.captured_at);
    }
    if (meta.fbc) attrs.hs_fbc = meta.fbc;
    if (meta.fbp) attrs.hs_fbp = meta.fbp;
    if (sess.utm_source) attrs.hs_utm_source = sess.utm_source;
    if (sess.utm_medium) attrs.hs_utm_medium = sess.utm_medium;
    if (sess.utm_campaign) attrs.hs_utm_campaign = sess.utm_campaign;
    if (sess.utm_content) attrs.hs_utm_content = sess.utm_content;
    if (sess.utm_term) attrs.hs_utm_term = sess.utm_term;
    try {
      _origFetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true,
      }).then(function () { cartAttrsWritten = true; }, function () {});
    } catch (_) {}
  }

  // ── Transport ────────────────────────────────────────────────────────────────

  function dynamicFields() {
    var click = storedClick();
    var meta = resolveMeta(click);
    return {
      gclid: (click && click.gclid) || undefined,
      gbraid: (click && click.gbraid) || undefined,
      wbraid: (click && click.wbraid) || undefined,
      fbclid: (click && click.fbclid) || undefined,
      srsltid: (click && click.srsltid) || undefined,
      fbc: meta.fbc || undefined,
      fbp: meta.fbp || undefined,
      click_captured_at: (click && click.captured_at) || undefined,
      cart_token: getCartToken() || undefined,
    };
  }

  function send(payload) {
    // fetch+keepalive (not sendBeacon): the endpoint is cross-origin and the body
    // is application/json, not a CORS-simple type — sendBeacon can't preflight.
    var body = JSON.stringify(Object.assign({}, payload, dynamicFields()));
    try {
      _origFetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        keepalive: true,
        mode: "cors",
        credentials: "omit",
      }).catch(function () {});
    } catch (_) {
      try { navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" })); } catch (__) {}
    }
  }

  function buildBase(sess, anonId) {
    return {
      anon_id: anonId,
      session_id: sess.id,
      utm_source: sess.utm_source,
      utm_medium: sess.utm_medium,
      utm_campaign: sess.utm_campaign,
      utm_content: sess.utm_content,
      utm_term: sess.utm_term,
      referrer: sess.referrer,
      landing_page: sess.landing_page,
    };
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  captureClick(); // persist any gclid/fbclid on this landing URL before first send
  var utms = parseUtms();
  var sess = getSession(utms);
  var anonId = getAnonId();
  var base = buildBase(sess, anonId);

  send(Object.assign({}, base, { event_type: "page_view", page_url: location.href }));

  var metaObj = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
  if (metaObj && metaObj.product) {
    send(Object.assign({}, base, {
      event_type: "product_view",
      page_url: location.href,
      product_id: String(metaObj.product.id || ""),
      product_title: metaObj.product.title || "",
      variant_id: String((metaObj.selectedVariantId || (metaObj.product.variants && metaObj.product.variants[0] && metaObj.product.variants[0].id)) || ""),
    }));
  }

  // ── Add-to-cart observation (fetch + XHR) ───────────────────────────────────
  // Observe-only: always delegates to the original, never alters the request.

  function isCartAddUrl(u) { return String(u || "").indexOf("/cart/add") !== -1; }
  function parseAddBody(body) {
    var out = { variantId: null, qty: 1 };
    function take(id, q) { if (id) { out.variantId = String(id); out.qty = parseInt(q || "1", 10) || 1; } }
    try {
      if (typeof body === "string") {
        var t = body.trim();
        if (t.charAt(0) === "{") { var parsed = JSON.parse(t); if (parsed && parsed.id) take(parsed.id, parsed.quantity); }
        else { var sp = new URLSearchParams(t); take(sp.get("id"), sp.get("quantity")); }
      } else if (typeof FormData !== "undefined" && body instanceof FormData) {
        take(body.get("id"), body.get("quantity"));
      } else if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        take(body.get("id"), body.get("quantity"));
      }
    } catch (_) {}
    return out;
  }
  function onCartAdd(variantId, qty) {
    // The `cart` cookie is set by the /cart/add response, so run this AFTER it
    // resolves. Write the deterministic cart attributes now that a cart exists,
    // and emit the fallback event (cart_token now available via dynamicFields).
    setCartAttributes(false);
    var extra = { event_type: "add_to_cart", page_url: location.href, quantity: qty || 1 };
    if (variantId) extra.variant_id = variantId;
    send(Object.assign({}, base, extra));
  }

  window.fetch = function (input, init) {
    // method/url may live on a Request object OR in init.
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var method = (init && init.method) || (input && input.method) || "GET";
    var isAdd = isCartAddUrl(url) && String(method).toUpperCase() === "POST";
    var parsed = isAdd ? parseAddBody(init && init.body) : null;
    var ret = _origFetch.apply(this, arguments);
    if (isAdd && ret && typeof ret.then === "function") {
      ret.then(function () { onCartAdd(parsed.variantId, parsed.qty); },
               function () { onCartAdd(parsed.variantId, parsed.qty); });
    }
    return ret;
  };

  // Older / jQuery cart drawers post /cart/add.js via XHR — observe those too.
  try {
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var _open = XHR.prototype.open;
      var _sendX = XHR.prototype.send;
      XHR.prototype.open = function (m, u) {
        this.__hsCartAdd = isCartAddUrl(u) && String(m || "").toUpperCase() === "POST";
        this.__hsBody = null;
        return _open.apply(this, arguments);
      };
      XHR.prototype.send = function (body) {
        if (this.__hsCartAdd) {
          var p = parseAddBody(body);
          this.addEventListener("loadend", function () { onCartAdd(p.variantId, p.qty); });
        }
        return _sendX.apply(this, arguments);
      };
    }
  } catch (_) {}

  // ── Purchase self-fire (GoKwik-proof, mobile-proof) ─────────────────────────
  // GoKwik completes the order in its overlay, then redirects mobile buyers to
  // the accounts subdomain where no merchant tag can run — so purchase tracking
  // that waits for a thank-you page never fires for most buyers. The reliable
  // moment is HERE: GoKwik announces order success to this parent page
  // (postMessage / Shopify.analytics.publish / dataLayer) BEFORE the redirect.
  // We listen on all three and fire the purchase tracking ourselves. GA4 and
  // Google Ads both dedup on transaction_id, so double-signals are safe.

  // Two-pass order-id resolution. Google/GA4 dedup is exact STRING equality, and
  // the Shopify channel pixel (desktop order-status page) + our server-side join
  // both key on the NUMERIC Shopify order id — so prefer that; only fall back to
  // the order NAME (with a leading '#' stripped) when no numeric id is present.
  var ORDER_ID_NUM_RE = /^(moid|order.?id|merchant.?order.?id|transaction.?id)$/i;
  var ORDER_KEY_RE = /(order.?(name|id|number)|merchant.?order.?id|transaction.?id)/i;
  // Two-pass ORDER-TOTAL resolution, for the same reason as the order id above.
  // GoKwik's COD success payload carries BOTH the order total AND the small COD
  // verification charge (`amount: 5`). A single regex + first-match-wins deepFind
  // returned whichever key the object happened to iterate first — which for COD
  // flows was the ₹5 charge, so the GA4/Ads valued conversion fired at ₹5 instead
  // of the real basket. Worse, Ads dedups per (action, transaction_id) first-in-
  // wins, so the ₹5 self-fire also BLOCKED the correctly-valued desktop channel
  // pixel for the same order. Prefer unambiguous order-total keys; only fall back
  // to the generic `amount`/`value` when no specific key exists.
  // ⚠ UNIT TRAP (verified on live data 2026-07-28): `total_price` is AMBIGUOUS —
  // GoKwik's Order_Placed_GK event sends it in RUPEES ("569.05") while Shopify's
  // cart.js sends the SAME KEY in PAISE (a ₹1,120.05 cart carries 112005). Taking
  // it at face value reported values 100× too high. It is handled separately by
  // normalizeAmount() below; every key in TOTAL_SPECIFIC_RE is verified rupees.
  var TOTAL_SPECIFIC_RE = /^(order_total|total_cart_amount|grand_total|totalValue|order_amount|total)$/i;
  var TOTAL_AMBIGUOUS_RE = /^(total_price)$/i;

  // Paise are always integers; rupee totals from these sources carry decimals.
  // An integer at or above the paise threshold is divided down; anything still
  // implausible is discarded rather than reported.
  function normalizeAmount(raw) {
    var str = String(raw).trim();
    var n = parseFloat(str);
    if (!isFinite(n) || n <= 0) return null;
    var isInteger = str.indexOf(".") === -1;
    if (isInteger && n >= 10000) n = n / 100; // paise → rupees
    return n;
  }
  var TOTAL_GENERIC_RE = /^(value|amount)$/i;
  // Absolute backstop: this store's AOV is ~₹1,000-1,800. Any resolved "total"
  // above this ceiling is a unit error (paise) or the wrong field — never a real
  // basket. Reporting nothing is strictly safer than reporting a 100× value:
  // an inflated conversion actively misteaches value-based bidding.
  var MAX_PLAUSIBLE_TOTAL = 25000;   // clears any plausible basket at AOV ₹1,000-1,800
  var MIN_PLAUSIBLE_TOTAL = 50;

  function deepFind(obj, keyRe, want, depth) {
    if (!obj || typeof obj !== "object" || depth > 4) return null;
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (keyRe.test(k)) {
        if (want === "num") {
          var n = parseFloat(v);
          if (isFinite(n) && n > 0 && n < 10000000) return n;
        } else if (v != null && (typeof v === "string" || typeof v === "number") && String(v).length > 0 && String(v).length < 64) {
          return String(v);
        }
      }
      if (v && typeof v === "object") {
        var r = deepFind(v, keyRe, want, depth + 1);
        if (r != null) return r;
      }
    }
    return null;
  }

  // Numeric variant of deepFind that returns the LARGEST match rather than the
  // first. Order-success payloads nest several plausible numbers (line-item price,
  // shipping, COD fee, basket total); the basket total is the largest of them.
  function deepFindMax(obj, keyRe, depth, normalise) {
    if (!obj || typeof obj !== "object" || depth > 4) return null;
    var best = null;
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (keyRe.test(k)) {
        var n = normalise ? normalizeAmount(v) : parseFloat(v);
        if (n !== null && isFinite(n) && n >= MIN_PLAUSIBLE_TOTAL && n <= MAX_PLAUSIBLE_TOTAL && (best === null || n > best)) best = n;
      }
      if (v && typeof v === "object") {
        var r = deepFindMax(v, keyRe, depth + 1, normalise);
        if (r != null && (best === null || r > best)) best = r;
      }
    }
    return best;
  }

  function looksLikeOrderSuccess(name) {
    return /order.?placed|gk_purchase|gokwik_purchase|^purchase$|purchase_gk|order.?success|order.?confirm/i.test(String(name || ""));
  }

  var purchaseFired = {};
  function onOrderSuccess(payload, source) {
    try {
      var oid = deepFind(payload, ORDER_ID_NUM_RE, "str", 0) || deepFind(payload, ORDER_KEY_RE, "str", 0);
      if (oid) oid = String(oid).replace(/^#/, "");
      // Specific keys first; generic (amount/value) only as a fallback. Both passes
      // collect the MAXIMUM match rather than the first, because a payload can carry
      // several same-named numbers at different depths (line-item price vs basket).
      // Trusted rupee keys → unit-ambiguous key (normalised) → generic last.
      var total = deepFindMax(payload, TOTAL_SPECIFIC_RE, 0);
      if (!total) total = deepFindMax(payload, TOTAL_AMBIGUOUS_RE, 0, true);
      if (!total) total = deepFindMax(payload, TOTAL_GENERIC_RE, 0, true);
      // Final guard at the fire site: never report an implausible value to Google.
      if (total && (total < MIN_PLAUSIBLE_TOTAL || total > MAX_PLAUSIBLE_TOTAL)) total = null;
      // Value-aware latch. GoKwik emits several success signals per order and only
      // some payloads carry the total: a valueless one must NOT lock out the valued
      // one that follows. "1" = order_placed only, "2" = valued gtag fired (final).
      var key = "_hs_pf_" + (oid || "session");
      var prev = purchaseFired[key] || "";
      try { prev = prev || sessionStorage.getItem(key) || ""; } catch (_) {}
      var canValue = Boolean(total) && typeof window.gtag === "function" && Boolean(window.HS_GA4_ID || window.HS_ADS_SEND_TO);
      if (prev === "2" || (prev === "1" && !canValue)) return;
      var state = canValue ? "2" : "1";
      purchaseFired[key] = state;
      try { sessionStorage.setItem(key, state); } catch (_) {}
      // 1. GA4 purchase with real value (GA4 drops later purchases w/ same transaction_id)
      try {
        if (typeof window.gtag === "function" && window.HS_GA4_ID && total) {
          window.gtag("event", "purchase", {
            send_to: window.HS_GA4_ID,
            transaction_id: oid || undefined,
            value: total,
            currency: "INR",
          });
        }
      } catch (_) {}
      // 2. Google Ads valued conversion (dedups on transaction_id per action —
      //    safe alongside the desktop order-status-page channel-pixel conversion)
      try {
        if (typeof window.gtag === "function" && window.HS_ADS_SEND_TO && total) {
          window.gtag("event", "conversion", {
            send_to: window.HS_ADS_SEND_TO,
            transaction_id: oid || undefined,
            value: total,
            currency: "INR",
          });
        }
      } catch (_) {}
      // 3. Our own stream (server writes the attribution bridge) — first fire only,
      //    so a valueless→valued upgrade doesn't emit a duplicate order_placed.
      if (prev !== "") return;
      send(Object.assign({}, base, {
        event_type: "order_placed",
        page_url: location.href,
        order_id: oid || undefined,
        order_total: total || undefined,
        metadata: { signal_source: source || null },
      }));
    } catch (_) {}
  }

  // Signal 1: window messages (GoKwik iframe → parent). Shapes vary; match by name.
  window.addEventListener("message", function (e) {
    try {
      var d = e.data;
      if (!d) return;
      var name = null, payload = d;
      if (typeof d === "object") {
        name = d.eventName || d.eventname || d.event || d.name || null;
        if (!name && d.data && typeof d.data === "object") name = d.data.eventname || d.data.eventName || d.data.event || null;
        if (d.type === "gokwik_events" && d.data) payload = d.data;
        // Structural signal: {type:"orderSuccess", response:{…}} — GoKwik's own
        // republish trigger, independent of the analytics-pipeline event names.
        if (!name && typeof d.type === "string") { name = d.type; payload = d.response || d.data || d; }
      } else if (typeof d === "string") {
        name = d;
      }
      if (name && looksLikeOrderSuccess(name)) onOrderSuccess(payload, "message:" + name);
    } catch (_) {}
  });

  // Signal 2: Shopify.analytics.publish — GoKwik publishes gokwik_purchase_event /
  // gokwik_events into Shopify's pixel bus; observe by wrapping publish.
  (function wrapPublish(tries) {
    try {
      var sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === "function" && !sa.__hsWrapped) {
        var orig = sa.publish;
        sa.publish = function (name, payload) {
          try {
            if (looksLikeOrderSuccess(name)) onOrderSuccess(payload, "publish:" + name);
            else if (/gokwik/i.test(String(name || ""))) {
              var inner = deepFind(payload, /^(eventname|eventName|name|event|stepName)$/i, "str", 0);
              if (looksLikeOrderSuccess(inner)) onOrderSuccess(payload, "publish:" + name + ":" + inner);
            }
          } catch (_) {}
          return orig.apply(this, arguments);
        };
        sa.__hsWrapped = true;
        return;
      }
    } catch (_) {}
    if ((tries || 0) < 60) setTimeout(function () { wrapPublish((tries || 0) + 1); }, 500);
  })(0);

  // ── GoKwik dataLayer bridge ─────────────────────────────────────────────────
  // GoKwik pushes checkout-initiated / order-placed / gk_purchase to dataLayer.
  // Mirror them into our stream (and re-assert cart attributes at checkout) —
  // this is the deterministic checkout/purchase signal, replacing DOM guessing
  // and the Shopify thank-you page that never renders under GoKwik.

  function handleDlEvent(o) {
    if (!o) return;
    var evt = o.event || (Array.isArray(o) ? o[0] : null);
    if (evt === "checkout-initiated" || evt === "begin_checkout") {
      setCartAttributes(true); // last chance before GoKwik overlay takes over
      send(Object.assign({}, base, { event_type: "checkout_start", page_url: location.href }));
    } else if (evt === "order-placed" || evt === "gk_purchase" || evt === "purchase") {
      onOrderSuccess(o, "dataLayer:" + evt);
    }
  }
  try {
    window.dataLayer = window.dataLayer || [];
    // Replay anything already pushed before we loaded.
    for (var i = 0; i < window.dataLayer.length; i++) { try { handleDlEvent(window.dataLayer[i]); } catch (_) {} }
    var _dlPush = window.dataLayer.push;
    window.dataLayer.push = function () {
      for (var j = 0; j < arguments.length; j++) { try { handleDlEvent(arguments[j]); } catch (_) {} }
      return _dlPush.apply(this, arguments);
    };
  } catch (_) {}

  // ── Native-checkout fallback (harmless under GoKwik) ────────────────────────
  // Hidden note_attributes on any Shopify /checkout form, for the rare native
  // path. GoKwik uses its own overlay, so this simply no-ops there.
  function injectCheckoutInputs() {
    try {
      var forms = document.querySelectorAll("form[action*='/checkout']");
      var click = storedClick();
      var fields = [
        ["utm_source", sess.utm_source], ["utm_medium", sess.utm_medium],
        ["utm_campaign", sess.utm_campaign], ["utm_content", sess.utm_content],
        ["utm_term", sess.utm_term], ["hs_session_id", sess.id], ["hs_anon_id", anonId],
        ["hs_gclid", click && click.gclid], ["hs_gbraid", click && click.gbraid],
        ["hs_wbraid", click && click.wbraid], ["hs_fbclid", click && click.fbclid],
      ];
      forms.forEach(function (form) {
        fields.forEach(function (pair) {
          if (!pair[1]) return;
          var nm = "attributes[" + pair[0] + "]";
          if (form.querySelector("input[name='" + nm + "']")) return;
          var inp = document.createElement("input");
          inp.type = "hidden"; inp.name = nm; inp.value = pair[1];
          form.appendChild(inp);
        });
      });
    } catch (_) {}
  }
  injectCheckoutInputs();
  document.addEventListener("click", function (e) {
    var el = e.target && e.target.closest
      ? e.target.closest("a[href*='/checkout'], button[name='checkout'], [data-gokwik], .gokwik-checkout-btn")
      : null;
    if (el) {
      setCartAttributes(true);
      send(Object.assign({}, base, { event_type: "checkout_start", page_url: location.href }));
      injectCheckoutInputs();
    }
  });
})();
